/**
 * Component Registry for Weidner Apartment Homes Theme
 *
 * Extends the base blue-haven theme registry with Weidner Apartment Homes specific overrides.
 */

import type { ComponentRegistry } from '@vibesites/runtime';
import { registry as baseRegistry } from '@vibesites/theme-blue-haven/registry';
import { Header } from './components/Header';
import { Footer } from './components/Footer';
import { HeroImageBG } from './components/HeroImageBG';
import { FutureResidentsClub } from './components/FutureResidentsClub';
import { FloorPlansIntro } from './components/FloorPlansIntro';
import { GalleryPhotos } from './components/GalleryPhotos';
import { InteractiveMapIntro } from './components/InteractiveMapIntro';

/**
 * Weidner Apartment Homes specific component overrides.
 * These components have been customized for the Weidner Apartment Homes brand.
 *
 * Header:
 *   Blue Haven's single-bar Header with Weidner-specific nav-page rules —
 *   `home` surfaced as an explicit nav link and the "Find Your Home /
 *   Features / Gallery & Tours" synthetic dropdown groupings. Dropdown
 *   hover swaps to a subtle fill + secondary-color label (mimicking
 *   mckinneyvillageapts.com). See the header comment in
 *   `src/components/Header.tsx` for the full diff notes.
 *
 * Footer:
 *   Blue Haven's Footer + three Weidner-specific additions — a "Text Us!"
 *   link between phone and address, and paired Weidner Cares / Love Notes
 *   program logos beneath the community logo. See the header comment in
 *   `src/components/Footer.tsx` for the diff notes.
 *
 * HeroImageBG:
 *   Blue Haven's image-background interior-page hero plus an OPTIONAL
 *   subtitle rendered directly beneath the H1 inside the hero image area.
 *   Non-opt-in pages (every existing HeroImageBG instance across the
 *   parent's pages.json) omit the `subtitle` prop and render pixel-
 *   identical to the parent — the Future Residents Club page surfaces a
 *   welcome blurb through it. See the header comment in
 *   `src/components/HeroImageBG.tsx`.
 *
 * FutureResidentsClub:
 *   Blue Haven's Future Residents Club section restyled to mirror the
 *   mckinneyvillageapts.com/future-residents-club reference — Vantage-set
 *   line icons, centered card content, brand-color titles, and a
 *   full-bleed secondary-color "How It Works" band with a luminance-
 *   driven contrast safety net for the on-secondary text. Schema keys
 *   unchanged so per-property editor state stays valid. See the header
 *   comment in `src/components/FutureResidentsClub.tsx`.
 *
 * FloorPlansIntro:
 *   Blue Haven intro + pricing verbiage (`custom_pricing_verbiage`) between
 *   the body copy and the CTA.
 *
 * GalleryPhotos:
 *   Blue Haven filterable gallery with a scrollable 2/3-column grid on mobile
 *   instead of the parent's single-image swipe carousel.
 *
 * InteractiveMapIntro:
 *   New company-level intro (heading + page blurb) placed above the
 *   inherited InteractiveMapSection. The parent page has no intro slot.
 */
const weidnerapartmenthomesOverrides: Partial<ComponentRegistry> = {
  Header,
  Footer,
  HeroImageBG,
  FutureResidentsClub,
  FloorPlansIntro,
  GalleryPhotos,
  InteractiveMapIntro,
};

/**
 * Final merged registry for Weidner Apartment Homes properties.
 * Inherits all components from the blue-haven theme, with Weidner Apartment Homes overrides.
 */
export const registry: ComponentRegistry = {
  ...baseRegistry,
  ...weidnerapartmenthomesOverrides,
};

export default registry;
